/**
 * DashboardLayout.tsx
 * ────────────────────
 * Layout principal du portail étudiant.
 *
 * - Sidebar FIXE (position: fixed) sur desktop, offset margin-left sur main
 * - Zéro border-radius — design system carré
 * - Animations framer-motion (fade-up sur le contenu, slide sur la sidebar mobile)
 * - Badge de notifications dynamique
 */
import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  Award,
  User,
  LogOut,
  Menu,
  X,
  ChevronRight,
  MessageSquare,
  Bell,
} from "lucide-react";
import { useAuth } from "contexts/AuthContext";
import { ROUTES } from "constants/routes";
import AppLogo from "components/common/AppLogo";
import { fadeUpVariants } from "lib/animations";

// ─── Tokens ───────────────────────────────────────────────────────────────────

const C_PRIMARY = "#ff6c00";
const C_DARK = "#111111";
const C_BORDER = "#e5e7eb";
const SIDEBAR_W = 252;

// ─── Sidebar ──────────────────────────────────────────────────────────────────

const SidebarEl = styled.aside<{ $open: boolean }>`
  width: ${SIDEBAR_W}px;
  background: ${C_DARK};
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 100;
  border-right: 1px solid #1a1a1a;

  @media (max-width: 1023px) {
    transform: translateX(${(p) => (p.$open ? "0" : `-${SIDEBAR_W}px`)});
    transition: transform 0.28s cubic-bezier(0.19, 1, 0.22, 1);
    box-shadow: ${(p) => (p.$open ? "8px 0 32px rgba(0,0,0,.4)" : "none")};
  }
`;

const SidebarLogo = styled.div`
  padding: 1.4rem 1.4rem 1rem;
  border-bottom: 1px solid #1c1c1c;
  flex-shrink: 0;
`;

const SidebarUser = styled.div`
  padding: 1rem 1.4rem;
  border-bottom: 1px solid #1c1c1c;
  flex-shrink: 0;
`;

const Avatar = styled.div`
  width: 34px;
  height: 34px;
  background: ${C_PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  flex-shrink: 0;
`;

const UserName = styled.p`
  font-size: 0.82rem;
  font-weight: 700;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const UserRole = styled.p`
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${C_PRIMARY};
  margin-top: 2px;
`;

const Nav = styled.nav`
  flex: 1;
  padding: 0.75rem 0.6rem;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #222;
  }
`;

const NavSection = styled.p`
  font-size: 0.58rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #333;
  padding: 0.75rem 0.75rem 0.3rem;
`;

const StyledLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.82rem 0.8rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.42);
  text-decoration: none;
  transition: all 0.14s;
  margin-bottom: 1px;
  position: relative;
  &:hover {
    background: rgba(255, 255, 255, 0.04);
    color: rgba(255, 255, 255, 0.85);
  }
  &.active {
    background: rgba(255, 108, 0, 0.12);
    color: ${C_PRIMARY};
    border-left: 3px solid ${C_PRIMARY};
    padding-left: calc(0.8rem - 3px);
  }
`;

const NotifBadge = styled.span`
  margin-left: auto;
  min-width: 18px;
  height: 18px;
  background: ${C_PRIMARY};
  color: white;
  font-size: 0.6rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

const SidebarBottom = styled.div`
  padding: 0.75rem 0.6rem;
  border-top: 1px solid #1c1c1c;
  flex-shrink: 0;
`;

const LogoutBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.62rem 0.8rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.28);
  background: none;
  border: none;
  cursor: pointer;
  width: 100%;
  transition: all 0.14s;
  &:hover {
    color: #f87171;
    background: rgba(248, 113, 113, 0.08);
  }
`;

// ─── Motion helper (cast pour éviter les erreurs TS sur children/onClick) ─────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

// ─── Overlay mobile ───────────────────────────────────────────────────────────

const MOverlay = styled(MDiv)`
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 90;
  @media (max-width: 1023px) {
    display: block;
  }
`;

// ─── Main area ────────────────────────────────────────────────────────────────

const MainArea = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  @media (min-width: 1024px) {
    margin-left: ${SIDEBAR_W}px;
  }
`;

const Topbar = styled.header`
  background: white;
  border-bottom: 1px solid ${C_BORDER};
  height: 56px;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const MenuBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: ${C_DARK};
  display: flex;
  align-items: center;
  padding: 4px;
  @media (min-width: 1024px) {
    display: none;
  }
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.74rem;
  font-weight: 600;
  color: #9ca3af;
`;

const ContentArea = styled.main`
  flex: 1;
  padding: 1.75rem 1.5rem;
  max-width: 1140px;
  width: 100%;
  margin: 0 auto;
  @media (min-width: 1024px) {
    padding: 2rem 2.5rem;
  }
`;

// ─── Nav config ───────────────────────────────────────────────────────────────

const NAV_MAIN = [
  {
    to: ROUTES.STUDENT_DASHBOARD,
    icon: LayoutDashboard,
    label: "Tableau de bord",
  },
  { to: ROUTES.STUDENT_PROCEDURES, icon: FolderKanban, label: "Mon dossier" },
  { to: ROUTES.STUDENT_DOCUMENTS, icon: FileText, label: "Documents" },
  { to: ROUTES.STUDENT_ATTESTATIONS, icon: Award, label: "Attestations AVI" },
];
const NAV_COMMS = [
  {
    to: ROUTES.STUDENT_MESSAGES,
    icon: MessageSquare,
    label: "Messages",
    badge: 0,
  },
  {
    to: ROUTES.STUDENT_NOTIFICATIONS,
    icon: Bell,
    label: "Notifications",
    badge: 0,
  },
];
const NAV_ACCOUNT = [
  { to: ROUTES.STUDENT_PROFILE, icon: User, label: "Mon profil" },
];

// ─── Component ────────────────────────────────────────────────────────────────

export interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  title,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);
  const initials = user
    ? `${(user.firstName ?? "?")[0]}${(user.lastName ?? "?")[0]}`.toUpperCase()
    : "?";

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f4f5f7" }}>
      {/* Mobile overlay */}
      <AnimatePresence>
        {open && (
          <MOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={close}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <SidebarEl $open={open}>
        <SidebarLogo>
          <AppLogo variant="dark" size="sm" href={ROUTES.HOME} />
        </SidebarLogo>

        {user && (
          <SidebarUser>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}
            >
              <Avatar>{initials}</Avatar>
              <div style={{ minWidth: 0 }}>
                <UserName>
                  {user.firstName} {user.lastName}
                </UserName>
                <UserRole>Espace étudiant</UserRole>
              </div>
            </div>
          </SidebarUser>
        )}

        <Nav>
          <NavSection>Principal</NavSection>
          {NAV_MAIN.map(({ to, icon: Icon, label }) => (
            <StyledLink
              key={to}
              to={to}
              end={to === ROUTES.STUDENT_DASHBOARD}
              onClick={close}
            >
              <Icon size={16} />
              {label}
            </StyledLink>
          ))}

          <NavSection>Communication</NavSection>
          {NAV_COMMS.map(({ to, icon: Icon, label, badge }) => (
            <StyledLink key={to} to={to} onClick={close}>
              <Icon size={16} />
              {label}
              {badge > 0 && <NotifBadge>{badge}</NotifBadge>}
            </StyledLink>
          ))}

          <NavSection>Compte</NavSection>
          {NAV_ACCOUNT.map(({ to, icon: Icon, label }) => (
            <StyledLink key={to} to={to} onClick={close}>
              <Icon size={16} />
              {label}
            </StyledLink>
          ))}
        </Nav>

        <SidebarBottom>
          <LogoutBtn
            onClick={() => {
              logout();
              navigate(ROUTES.LOGIN);
            }}
          >
            <LogOut size={16} />
            Se déconnecter
          </LogoutBtn>
        </SidebarBottom>
      </SidebarEl>

      {/* Main */}
      <MainArea>
        <Topbar>
          <MenuBtn onClick={() => setOpen((o) => !o)}>
            {open ? <X size={20} /> : <Menu size={20} />}
          </MenuBtn>
          <Breadcrumb>
            <span>Espace étudiant</span>
            {title && (
              <>
                <ChevronRight size={12} />
                <span style={{ color: C_DARK, fontWeight: 700 }}>{title}</span>
              </>
            )}
          </Breadcrumb>
          <div style={{ width: 28 }} />
        </Topbar>

        <ContentArea>
          <MDiv
            key={title}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
          >
            {children}
          </MDiv>
        </ContentArea>
      </MainArea>
    </div>
  );
};

export default DashboardLayout;
